/**
 * ═══ WHAT THE ORGANISER CAN DO TO THEIR OWN HTML ═══
 *
 * The toolbar of the block editor and the lists its dropdowns offer — data, kept in a
 * `.ts` and out of the component, for one reason above all:
 *
 * ⚠️ A BUTTON NAME THAT DOES NOT EXIST DOES NOT DEGRADE, IT THROWS. `create` refuses
 * to build an editor whose toolbar mentions a button it cannot find ("The button name
 * of a plugin that does not exist. [align]") and the organiser gets the fallback
 * message instead of an editor — the whole field, not the one button. And the names
 * are THIS version's: we are on SunEditor 3.x on the core, the back office is on 2.47
 * through the React wrapper, and the two disagree exactly where it hurts —
 * `hiliteColor` is `backgroundColor` here, `formatBlock` is `blockStyle`,
 * `horizontalRule` is `hr`. So `htmlEditor.test.ts` checks every name against the
 * package on disk: the next rename is caught by `npm test`, not by a client.
 *
 * ⚠️ THE SAME PALETTE AS THE EMAILS, and that is the requirement rather than a taste:
 * an organiser who has laid out an email in the back office (`bo/src/components/
 * SettingsWysiwyg.jsx`) expects to find the same tools here — a typeface, a size, a
 * colour. Everything that editor offers is offered here, translated to 3.x names.
 * What is NOT here is what this field already does better elsewhere: `preview` and
 * `showBlocks`, because the row shows the block rendered and the HTML mode shows the
 * markup — both are one click away, permanently, without a toolbar button.
 */

/** A `'|'` is a separator, not a button; nested arrays are the toolbar's groups. */
export const TOOLBAR: (string | string[])[] = [
  ["undo", "redo"],
  "|",
  ["blockStyle", "font", "fontSize"],
  "|",
  ["bold", "italic", "underline", "strike", "textStyle"],
  "|",
  ["fontColor", "backgroundColor", "removeFormat"],
  "|",
  ["align", "lineHeight", "list", "outdent", "indent"],
  "|",
  ["hr", "table", "link", "image"],
];

/**
 * ⚠️ ONE FAMILY PER ENTRY, NEVER A STACK. The plugin quotes any value holding a
 * non-word character before writing it — `Helvetica, Arial, sans-serif` would be
 * emitted as `font-family: "Helvetica, Arial, sans-serif"`, a single name nothing can
 * match, and the text would silently keep the page's font. The label shown is the
 * entry itself, so it is also what the organiser reads.
 *
 * Web-safe names only: what is written here can end up in a badge or an email, far
 * from a browser that would fetch a font for us. Leaving the dropdown on « Défaut »
 * keeps the event's own typeface, which is why no house font is listed.
 */
export const FONTS = [
  "Arial",
  "Courier New",
  "Georgia",
  "Helvetica",
  "Impact",
  "Tahoma",
  "Times New Roman",
  "Trebuchet MS",
  "Verdana",
];

/** The back office's list for emails, to the value — see `SettingsWysiwyg.jsx`. */
export const LINE_HEIGHTS = [
  { text: "0.5", value: "0.5" },
  { text: "0.75", value: "0.75" },
  { text: "1", value: "1" },
  { text: "1.15", value: "1.15" },
  { text: "1.25", value: "1.25" },
  { text: "1.5", value: "1.5" },
  { text: "1.75", value: "1.75" },
  { text: "2", value: "2" },
  { text: "2.25", value: "2.25" },
  { text: "2.5", value: "2.5" },
  { text: "3", value: "3" },
  { text: "3.5", value: "3.5" },
  { text: "4", value: "4" },
];

/**
 * The toolbar and the options its dropdowns read, ready to spread into `create`.
 *
 * ⚠️ PLUGIN OPTIONS SIT AT THE TOP LEVEL of the editor's options, keyed by the plugin
 * name (`font`, `fontSize`, `lineHeight`) — not under a `pluginOptions` bag.
 *
 * `sizeUnit` is stated rather than left to the default: the default is the first entry
 * of `fontSizeUnits`, so a size in `pt` or `em` is one unrelated option away — and a
 * block written in `pt` would not scale with the page.
 */
export const TOOLS = {
  buttonList: TOOLBAR,
  font: { items: FONTS },
  fontSize: { sizeUnit: "px" },
  lineHeight: { items: LINE_HEIGHTS },
};

/**
 * ⚠️ LA HAUTEUR DE CE QU'ON ÉDITE — une seule fois, parce que DEUX champs la portent :
 * le canevas de SunEditor et le champ HTML brut. Les deux modes sont le même champ, et
 * basculer de l'un à l'autre ne doit pas faire sauter la boîte.
 *
 * Bornée en haut et en bas : l'ordinateur d'un organisateur ne fait pas toujours
 * 1000 px de haut, et l'entête plus le pied de la modale prennent ~130 px au-dessus.
 *
 * ⚠️ ET ELLE CONTIENT DES CHIFFRES, ce qui n'est pas un détail : SunEditor n'arme la
 * poignée de redimensionnement de sa barre d'état que si `/\d+/` reconnaît la hauteur
 * (`eventOrchestrator.__addStatusbarEvent`). Un `var(--…)`, pourtant plus propre, la
 * désactivait sans rien dire.
 */
export const CANVAS_HEIGHT = "clamp(320px, 58vh, 620px)";

/** The toolbar flattened to the names `create` will look up — separators dropped. */
export function buttonsOf(toolbar: (string | string[])[]): string[] {
  return toolbar.flat().filter((name) => name !== "|");
}

/**
 * ═══ RAPATRIER LES CALQUES DE L'ÉDITEUR DANS LA MODALE ═══
 *
 * ⚠️ LES MENUS NE SONT PAS DANS L'ÉDITEUR. SunEditor accroche à `document.body` un
 * « carrier wrapper » — le plateau qui porte TOUS ses calques : les listes de ses
 * menus déroulants (`initDropdownTarget` les met dans le `.se-menu-tray`, qui est
 * dedans), ses propres modales de lien et d'image, ses alertes. L'éditeur, lui, reste
 * où on l'a monté.
 *
 * Dans une page ordinaire cela ne se voit pas. Dans un `<dialog>` ouvert par
 * `showModal()`, c'est fatal, et la norme le dit mot pour mot : le document est
 * « blocked by a modal dialog », tout ce qui n'est pas un descendant de la modale
 * devient INERTE, et pour un nœud inerte « hit-testing must act as if the
 * `pointer-events` CSS property were set to `none` ». Le menu s'OUVRE donc et se voit
 * — c'est un `popover`, il est dans le top layer, au-dessus de la modale — mais le
 * clic le TRAVERSE et atterrit sur le canevas en dessous, qui lui n'est pas inerte.
 * Cliquer « Georgia » ne faisait rien, et rien non plus dans la console.
 *
 * On déplace donc le plateau DANS le `<dialog>` : descendant de la modale, il cesse
 * d'être inerte. Il n'y perd pas sa place à l'écran — ses calques sont des `popover`,
 * et un élément du top layer se positionne sur le bloc conteneur initial et non sur
 * ses ancêtres, ce que les coordonnées-document de `setRelPosition` attendent déjà.
 * Seule l'inertie change.
 *
 * ⚠️ ET C'EST LE CHEMIN DE LA BIBLIOTHÈQUE, pas une classe CSS devinée :
 * `$.contextProvider.carrierWrapper` est déclaré dans les typages du paquet.
 * `htmlEditor.test.ts` monte un vrai éditeur et vérifie que ce chemin mène bien au
 * plateau qui porte les menus — la prochaine version qui le déplace est prise par
 * `npm test`, pas par un organisateur qui clique dans le vide.
 *
 * @returns le plateau déplacé, ou `null` s'il n'y avait pas de modale au-dessus.
 */
export function adoptMenus(
  instance: unknown,
  host: Element,
): HTMLElement | null {
  const carrier = (
    instance as {
      $?: { contextProvider?: { carrierWrapper?: unknown } };
    } | null
  )?.$?.contextProvider?.carrierWrapper;
  const box = host.closest("dialog");
  if (!box || !(carrier instanceof HTMLElement)) return null;
  box.appendChild(carrier);
  return carrier;
}

/**
 * ═══ AUCUNE IMAGE N'ENTRE EN BASE64 ═══
 *
 * Sans adresse d'envoi, SunEditor lit l'image et l'écrit en base64 dans le HTML : c'est ce
 * qui a gonflé les réglages de l'événement 744 (voir `api/upload.ts`). Deux portes à tenir :
 *
 *   — `onImageUploadBefore` : le bouton image, et une image collée ou glissée comme
 *     FICHIER. On envoie nous-mêmes (`uploadEditorImage`), puis on repasse par
 *     `submitURL` : SunEditor pose alors l'image par son adresse. Une image donnée par
 *     adresse passe telle quelle.
 *   — `onPaste` : du HTML collé depuis un document, qui porte déjà ses images en base64.
 *     Elles partent aussi, et le HTML collé n'en garde que l'adresse.
 *
 * ⚠️ UNE IMAGE QUI NE PART PAS EST RETIRÉE, PAS GARDÉE EN BASE64 : c'est exactement ce
 * qu'on empêche. L'organisateur en est averti.
 */
type ImageInfo = { url?: string; files?: unknown };
type EditorKernel = {
  plugins: { image?: { submitURL: (url: string) => Promise<unknown> } };
  ui?: { alertOpen?: (message: string, kind: "" | "error" | "success") => void };
};

const isImageFile = (f: unknown): f is File =>
  typeof (f as File | null)?.type === "string" && (f as File).type.startsWith("image/");

export function interceptImageUpload(upload: (file: File) => Promise<string>) {
  return async ({ $, info }: { $: EditorKernel; info: ImageInfo }) => {
    if (info?.url) return true;
    const files = Array.from((info?.files as ArrayLike<unknown>) ?? []).filter(isImageFile);
    if (!files.length) return false;
    void (async () => {
      for (const file of files) {
        try {
          const url = await upload(file);
          await $.plugins.image?.submitURL(url);
        } catch (cause) {
          $.ui?.alertOpen?.(
            cause instanceof Error && cause.message
              ? cause.message
              : "L'image n'a pas pu être envoyée.",
            "error",
          );
        }
      }
    })();
    /* `undefined` : « je m'en occupe », et SunEditor ne prend pas son chemin base64. */
    return undefined;
  };
}

const INLINE_IMG = /<img\b[^>]*?\bsrc\s*=\s*(["'])(data:image\/[^"']*)\1[^>]*>/gi;

/** Du HTML qui porte au moins une image en base64. */
export function hasInlineImages(html: string): boolean {
  return /<img\b[^>]*\bsrc\s*=\s*["']data:image\//i.test(html);
}

/**
 * LES IMAGES BASE64 D'UN HTML COLLÉ, ENVOYÉES ET REMPLACÉES PAR LEUR ADRESSE. Une même
 * image collée deux fois ne part qu'une fois ; une image qui ne part pas est retirée, et
 * comptée dans `failed` pour le dire.
 */
export async function replaceInlineImages(
  html: string,
  upload: (file: File) => Promise<string>,
  toFile: (dataUrl: string, name: string) => File | null,
): Promise<{ html: string; failed: number }> {
  const urls = new Map<string, string | null>();
  let n = 0;
  for (const match of html.matchAll(INLINE_IMG)) {
    const dataUrl = match[2]!;
    if (urls.has(dataUrl)) continue;
    n += 1;
    const file = toFile(dataUrl, `image-collee-${n}`);
    try {
      urls.set(dataUrl, file ? await upload(file) : null);
    } catch {
      urls.set(dataUrl, null);
    }
  }
  let failed = 0;
  const out = html.replace(INLINE_IMG, (tag, _quote: string, dataUrl: string) => {
    const url = urls.get(dataUrl);
    if (!url) {
      failed += 1;
      return "";
    }
    return tag.replace(dataUrl, () => url);
  });
  return { html: out, failed };
}
